import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@design-system';

import { ArrowRight, Building2 } from 'lucide-react';

import { formatCurrency } from '@shared';

import { CcrcTypeAProductDetailsHoverCard } from './ccrc-type-a-product-details';
import { type CcrcTypeAProductMetrics } from '../lib/ccrc-type-a-metrics';
import { type CcrcTypeAPricingBreakdown } from '../lib/ccrc-type-a-pricing';

interface CcrcTypeAProductCardProps {
  product: CcrcTypeAProduct;
  pricing: CcrcTypeAPricingBreakdown;
  metrics: CcrcTypeAProductMetrics;
  onSelect: () => void;
}

export function CcrcTypeAProductCard({
  product,
  pricing,
  metrics,
  onSelect,
}: CcrcTypeAProductCardProps) {
  const { projectedCoverage, cost, totalRoi, breakdown } = metrics;

  const hasMetrics = cost > 0;
  const roiText = `${(totalRoi * 100).toFixed(2)}% Total ROI`;

  return (
    <Card className="flex w-full flex-col gap-0 p-6">
      <CardHeader className="flex flex-row items-center gap-4 px-0 pb-6 pt-0">
        <div className="bg-background flex size-16 shrink-0 items-center justify-center rounded-md border">
          <Building2 className="text-primary size-8" />
        </div>
        <div className="min-w-0 space-y-0.5">
          <CardTitle className="text-primary-dark">{product.productName}</CardTitle>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <CardDescription className="text-foreground">{product.communityName}</CardDescription>
            <span
              className="bg-border h-3 w-px"
              aria-hidden
            />
            <CcrcTypeAProductDetailsHoverCard
              product={product}
              pricing={pricing}
              metrics={metrics}
            >
              <button
                type="button"
                className="text-muted-foreground hover:text-primary focus-visible:ring-primary cursor-help text-sm underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2"
              >
                Product Details
              </button>
            </CcrcTypeAProductDetailsHoverCard>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 gap-12 border-t px-0 py-6">
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium">Projected Coverage</p>
          <p className="text-xl font-bold text-gray-950">{formatCurrency(projectedCoverage)}</p>
          {breakdown && (
            <p className="text-foreground mb-4 text-sm">
              You {formatCurrency(breakdown.primary.projectedCoverage)} · Partner{' '}
              {formatCurrency(breakdown.partner.projectedCoverage)}
            </p>
          )}
          {hasMetrics && (
            <div className="pt-0.5">
              <span className="bg-primary/10 text-primary inline-block w-fit rounded px-1.5 py-0.5 text-xs font-medium">
                {roiText}
              </span>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium">Cost</p>
          <p className="text-xl font-bold text-gray-950">{formatCurrency(cost)}</p>
          {breakdown && (
            <p className="text-foreground text-sm">
              You {formatCurrency(breakdown.primary.cost)} · Partner{' '}
              {formatCurrency(breakdown.partner.cost)}
            </p>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-0">
        <Button
          className="w-full"
          onClick={onSelect}
        >
          Model this in my plan
          <ArrowRight />
        </Button>
      </CardFooter>
    </Card>
  );
}
